import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { translations, type Language } from '@/i18n/translations'

interface LanguageContextValue {
  lang: Language
  setLang: (lang: Language) => void
  T: typeof translations['en']
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

function getInitialLang(): Language {
  try {
    const stored = localStorage.getItem('tll_lang')
    if (stored === 'en' || stored === 'tr') return stored
  } catch { /* ignore */ }
  return 'tr'
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Language>(getInitialLang)

  // index.html ships with lang="tr", and CSS text-transform follows the element
  // language: under Turkish casing rules `i` uppercases to `İ`, so every
  // uppercase English heading rendered as "MEMBERSHİP" / "COMMUNİTY". Keeping
  // <html lang> in step with the chosen language fixes all of them at once, and
  // tells screen readers which language they are reading.
  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  const setLang = (l: Language) => {
    setLangState(l)
    try { localStorage.setItem('tll_lang', l) } catch { /* ignore */ }
  }

  return (
    <LanguageContext.Provider value={{ lang, setLang, T: translations[lang] }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useLanguage must be used inside LanguageProvider')
  return ctx
}
